import { gql, useQuery } from '@apollo/client';
import IconOutlink from '@apollo/icons/default/IconOutlink.svg';
import { Badge, Link } from '@apollo/orbit';
import { LocationDescriptorObject } from 'history';
import React from 'react';
import {
  Link as ReactRouterLink,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';

import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { useGraphVisibility } from 'src/app/graph/hooks/useGraphVisibility';
import {
  explorerRouteConfig,
  schemaRouteConfig,
  variantHomepageRouteConfig,
} from 'src/app/graph/routes';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';

export const PublicGraphWidget = ({ graphRef }: { graphRef: GraphRef }) => {
  const { data, loading } = useQuery<
    GraphQLTypes.PublicGraphWidgetQuery,
    GraphQLTypes.PublicGraphWidgetQueryVariables
  >(
    gql`
      query PublicGraphWidgetQuery($graphId: ID!, $graphVariant: String!) {
        service(id: $graphId) {
          id
          account {
            id
          }
          roles {
            canManageVariants
          }
          variant(name: $graphVariant) {
            id
            isPublic
          }
        }
      }
    `,
    {
      variables: graphRef,
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  const graphVisibilityType = useGraphVisibility();
  const isPublicRoute = graphVisibilityType === 'public';

  const isPublicVariant = !!data?.service?.variant?.isPublic;
  // Public viewers will not have access to the account for a graph
  const hasPrivateAccess = !!data?.service?.account;

  const location = useLocation();
  const isSchemaRoute = !!useRouteMatch(schemaRouteConfig.definition);
  const isExplorerRoute = !!useRouteMatch(explorerRouteConfig.definition);
  const publicLocation: LocationDescriptorObject<unknown> =
    isSchemaRoute || isExplorerRoute
      ? {
          ...location,
          pathname: location.pathname.replace(/^\/graph\//, '/public/'),
        }
      : variantHomepageRouteConfig.location({
          ...graphRef,
          graphVisibilityType: 'public',
        });

  if (loading) {
    return null;
  }

  if (isPublicRoute) {
    return (
      <Tooltip
        interactive
        label={
          <>
            This variant is public.{' '}
            <Link isExternal to={DocsRouter.path('PublicGraphs')}>
              Learn more
            </Link>
          </>
        }
        placement="bottom"
      >
        <Badge>Public</Badge>
      </Tooltip>
    );
  }
  if (isPublicVariant && hasPrivateAccess) {
    return (
      <ReactRouterLink
        to={publicLocation}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center"
      >
        <Tooltip
          placement="bottom"
          label="This variant is public. Click to switch to the public facing view."
        >
          <Badge icon={<IconOutlink />}>Public</Badge>
        </Tooltip>
      </ReactRouterLink>
    );
  }
  return null;
};
